import type { SvgIconComponent } from "@mui/icons-material";
import {
  Badge,
  Home,
  LocalPharmacy,
  Medication,
} from "@mui/icons-material";
import type { Palette } from "@mui/material/styles";

/** Identificadores dos tipos de documento — adicione novos itens nesta união. */
export type PatientSupportingDocumentTypeId =
  | "anvisa_import_authorization"
  | "medical_prescription"
  | "personal_document"
  | "proof_of_residence";

export type PatientSupportingDocumentAccentKey = keyof Pick<
  Palette,
  "primary800" | "secondary800" | "success700" | "warning700"
>;

/** Configuração de cada tipo — centralizada para facilitar novos documentos. */
export interface PatientSupportingDocumentTypeConfig {
  id: PatientSupportingDocumentTypeId;
  title: string;
  description: string;
  Icon: SvgIconComponent;
  accentKey: PatientSupportingDocumentAccentKey;
  accept: string;
  acceptHint: string;
  maxSizeMb: number;
}

export interface PatientSupportingDocumentRecord {
  typeId: PatientSupportingDocumentTypeId;
  fileName: string;
  /** Identificação opcional informada pelo paciente e salva em DocumentReference.description. */
  description?: string;
  mimeType: string;
  /** Data URL (base64) do arquivo — mantido em memória para preview nesta entrega. */
  dataUrl: string;
  /** URL do Binary no MHD — fallback quando o conteúdo não puder ser carregado em base64. */
  binaryUrl?: string;
  notes: string;
  updatedAt: string;
  /** Data de envio do arquivo (DocumentReference.date). */
  sentAt?: string;
  /** Data da remoção lógica (meta.lastUpdated) — só em cancelados. */
  removedAt?: string;
  /** Id do DocumentReference retornado pelo POST MHD (quando persistido). */
  documentReferenceId?: string;
  /** Identifier official do documento atual — usado no relatesTo ao substituir. */
  previousIdentifier?: { system: string; value: string };
  /** docStatus FHIR — "cancelled" no histórico de removidos. */
  docStatus?: string;
  /** Data (attester[].time) da verificação humana pelo profissional. */
  attestedAt?: string;
  /** attester[].party.reference da organização que verificou. */
  attestedByReference?: string;
  /** true quando o DocumentReference possui attester profissional. */
  isAttested?: boolean;
  /** Documento recusado pelo profissional (docStatus rejected). */
  isRejected?: boolean;
  /** Motivo da recusa (`DocumentReference.docStatus.text`). */
  rejectionReason?: string;
  validUntil?: string;
  transactionId?: string;
  isExpired?: boolean;
}

/**
 * Cada categoria mantém seu próprio histórico. A lista fica ordenada do
 * documento mais recente para o mais antigo.
 */
export type PatientSupportingDocumentsStore = Partial<
  Record<PatientSupportingDocumentTypeId, PatientSupportingDocumentRecord[]>
>;

/**
 * Catálogo de documentos solicitados.
 * Para incluir um novo tipo, basta acrescentar um item neste array.
 */
export const PATIENT_SUPPORTING_DOCUMENT_TYPES: PatientSupportingDocumentTypeConfig[] =
  [
    {
      id: "anvisa_import_authorization",
      title: "Autorização Anvisa",
      description:
        "Autorização de importação de medicamento emitida pela Anvisa.",
      Icon: LocalPharmacy,
      accentKey: "secondary800",
      accept: ".pdf,.jpg,.jpeg,.png",
      acceptHint: "PDF, JPG ou PNG — até 5 MB",
      maxSizeMb: 5,
    },
    {
      id: "medical_prescription",
      title: "Receita médica",
      description: "Receita ou prescrição médica válida para o tratamento.",
      Icon: Medication,
      accentKey: "primary800",
      accept: ".pdf,.jpg,.jpeg,.png",
      acceptHint: "PDF, JPG ou PNG — até 5 MB",
      maxSizeMb: 5,
    },
    {
      id: "personal_document",
      title: "Documento pessoal",
      description: "RG, CNH, passaporte ou outro documento de identificação.",
      Icon: Badge,
      accentKey: "success700",
      accept: ".pdf,.jpg,.jpeg,.png",
      acceptHint: "PDF, JPG ou PNG — até 5 MB",
      maxSizeMb: 5,
    },
    {
      id: "proof_of_residence",
      title: "Comprovante de residência",
      description:
        "Conta de consumo, contrato ou comprovante recente de endereço.",
      Icon: Home,
      accentKey: "warning700",
      accept: ".pdf,.jpg,.jpeg,.png",
      acceptHint: "PDF, JPG ou PNG — até 5 MB",
      maxSizeMb: 5,
    },
  ];

export function getPatientSupportingDocumentType(
  typeId: PatientSupportingDocumentTypeId,
): PatientSupportingDocumentTypeConfig {
  return PATIENT_SUPPORTING_DOCUMENT_TYPES.find((item) => item.id === typeId)!;
}
